import { api_configuration } from "../../common";
import DestructorTree from "../DestructorTree";
import api_views = api_configuration.libraries.gui_definition_lib.types.views.alan_api;
import client_state_api = api_configuration.types.client_state.alan_api;
import api_widget = api_configuration.libraries.gui_definition_lib.types.widget.alan_api;
import client_state_binder = require("./client_state_binder");
import ImplementationVisitorContext from "./ImplementationVisitorContext";
import { isDefined, ensure } from "../validation";
import { wrapNode } from "./wrapNode";
import * as modifications from "../state/modifications";
import { registerModelBindingPropertyPath } from "../state/registerClientState";
import { initializeQueryWithModelBinding } from "./bindQuery";
import { visitExpression, visitOptionalContextDefinition } from "./visit_widget_implementation_expression";
import { visit_control_binding } from "./visit_control_binding";

export function visit_widget_implementation(
	parent_dom_element: () => HTMLElement,
	widget: api_widget.Cwidget,
	widget_configuration: api_views.Cgui_widget_configuration_node,
	client_state_context: client_state_api.AlanObject,
	destructor: DestructorTree,
	view_root_dom_element: () => HTMLElement,
	client_view: client_state_api.Copen_views,
	model_binding: api_views.Cmodel_binding | undefined,
	entry_keys: string[]
): void {
	if (global.alan_debug) {
		parent_dom_element = pathDomElementWithContext(parent_dom_element, client_state_context);
	}

	parent_dom_element = patchDomElementWithWidgetName(parent_dom_element, widget.key!);
	walkConfigurationNode(widget_configuration, client_state_context, destructor);
	walkWidget(widget.properties.widget, destructor, {
		node: client_view.properties.view_context.output.result_node(),
		gui_config: widget_configuration,
		entry_keys: entry_keys,
	});
	let implementation_context = widget.properties.binding_context
		.switch({
			none: function () {
				return ImplementationVisitorContext.create(
					client_state_context,
					widget_configuration,
					model_binding,
					null,
					view_root_dom_element,
					client_view,
					entry_keys
				);
			},
			select: function ($) {
				const intermediate_visitor_context = ImplementationVisitorContext.create(
					client_state_context,
					widget_configuration,
					model_binding,
					(client_state_binder.binding as any)[$.properties.binding.ref.key],
					view_root_dom_element,
					client_view,
					entry_keys
				);
				return $.properties.switch_client_binding_context.switch({
					yes: function ($) {
						const binding_name = $.properties.constrained_on_containing_binding.state.node.output.client_binding().key;
						return isDefined(intermediate_visitor_context.client_state_binder_AsPropertiesBinding.bindings)[
							binding_name
						](intermediate_visitor_context, destructor);
					},
					no: intermediate_visitor_context,
				});
			},
		})
		.push_widget_implementation_frame();

	implementation_context = visitOptionalContextDefinition(
		widget.properties.optional_context_definition,
		implementation_context,
		destructor
	);

	visitExpression(widget.properties.expression).subscribe(
		implementation_context,
		{
			add: (control_binding: api_widget.Ccontrol_binding, sub_implementation_context, sub_destructor, wrap) => {
				const wrapped_parent_dom_element = wrap ? wrapNode(parent_dom_element, sub_destructor) : parent_dom_element;
				visit_control_binding(
					control_binding,
					wrapped_parent_dom_element,
					sub_implementation_context,
					sub_destructor,
					false
				);
			},
		},
		destructor,
		undefined
	);
}

function walkConfigurationAttribute(
	attribute: api_views.Cgui_widget_configuration_attribute,
	client_state_context: client_state_api.AlanObject,
	destructor: DestructorTree
) {
	switch (attribute.properties.type.state.name) {
		case "configuration": {
			const configuration_attribute = attribute.properties.type.state.node;
			switch (configuration_attribute.properties.type.state.name) {
				case "state group": {
					const state_group_node = configuration_attribute.properties.type.state.node;
					walkConfigurationNode(state_group_node.properties.configuration, client_state_context, destructor);
					break;
				}
				case "list":
					walk_gui_widget_configuration_list(
						configuration_attribute.properties.type.state.node.properties.list,
						(configuration) => {
							walkConfigurationNode(configuration, client_state_context, destructor);
						}
					);
					break;
				case "number":
					break;
				case "text":
					break;
				case "states list":
					for (const $ of configuration_attribute.properties.type.state.node.properties.states.values()) {
						walkConfigurationNode($.properties.configuration, client_state_context, destructor);
					}
					break;
				default:
					unexpectedState(configuration_attribute.properties.type.state);
			}
			break;
		}
		case "model binding": {
			const model_binding_attribute = attribute.properties.type.state.node;
			switch (model_binding_attribute.properties.type.state.name) {
				case "entity":
					walkConfigurationNode(
						model_binding_attribute.properties.type.state.node.properties.configuration,
						client_state_context,
						destructor
					);
					break;
				case "current node":
					walkConfigurationNode(
						model_binding_attribute.properties.type.state.node.properties.configuration,
						client_state_context,
						destructor
					);
					break;
				case "collection":
					walkConfigurationNode(
						model_binding_attribute.properties.type.state.node.properties.configuration,
						client_state_context,
						destructor
					);
					break;
				case "ordered graph":
					break;
				case "sort property":
					break;
				case "query": {
					const query_type = model_binding_attribute.properties.type.state.node;
					bindToQuery(client_state_context, query_type, destructor);
					// create optional subscription for query (that can be used in the client state binder)
					break;
				}
				case "group":
					walkConfigurationNode(
						model_binding_attribute.properties.type.state.node.properties.configuration,
						client_state_context,
						destructor
					);
					break;
				case "state group":
					walkConfigurationNode(
						model_binding_attribute.properties.type.state.node.properties.configuration,
						client_state_context,
						destructor
					);
					break;
				case "state":
					walkConfigurationNode(
						model_binding_attribute.properties.type.state.node.properties.configuration,
						client_state_context,
						destructor
					);
					break;
				case "state rule":
					walkConfigurationNode(
						model_binding_attribute.properties.type.state.node.properties.configuration,
						client_state_context,
						destructor
					);
					break;
				case "command":
					break;
				case "action":
					break;
				case "file":
					break;
				case "number":
					break;
				case "query file":
					break;
				case "query stategroup":
					break;
				case "query number":
					break;
				case "query text":
					break;
				case "text":
					break;
				case "window":
					break;
				case "report":
					break;
				case "report text parameter":
					break;
				case "report number parameter":
					break;
				case "joined entry":
					break;
				case "user":
					break;
				default:
					unexpectedState(model_binding_attribute.properties.type.state);
			}
			break;
		}
		case "view":
			break;
		case "widget":
			break;
		case "window":
			break;
		default:
			unexpectedState(attribute.properties.type.state);
	}
}

function walkConfigurationNode(
	config_node: api_views.Cgui_widget_configuration_node,
	client_state_context: client_state_api.AlanObject,
	destructor: DestructorTree
) {
	for (const attribute of config_node.properties.configuration.values()) {
		walkConfigurationAttribute(attribute.properties.attribute, client_state_context, destructor);
	}
}

function walkWidget(
	config: api_widget.Cwidget_configuration_node,
	destructor: DestructorTree,
	context: {
		node: client_state_api.Cnode;
		gui_config: api_views.Cgui_widget_configuration_node;
		entry_keys: string[];
	}
) {
	for (const attribute of config.properties.attributes.values()) {
		switch (attribute.properties.type.state.name) {
			case "binding": {
				const $ = attribute.properties.type.state.node;
				walkWidget($.properties.node, destructor, context);
				break;
			}
			case "configuration": {
				const $ = attribute.properties.type.state.node;
				switch ($.properties.type.state.name) {
					case "list": {
						const list_property = $.properties.type.state.node;
						walkWidget(list_property.properties.node, destructor, context);
						break;
					}
					case "number": {
						const number_property = $.properties.type.state.node;
						modifications.registerNumberDestructor(
							context.node,
							context.gui_config,
							number_property,
							destructor,
							context.entry_keys
						);
						break;
					}
					case "state group": {
						const state_group_property = $.properties.type.state.node;
						modifications.registerStateDestructor(
							context.node,
							context.gui_config,
							state_group_property,
							destructor,
							context.entry_keys
						);
						break;
					}
					case "text": {
						const text_property = $.properties.type.state.node;
						modifications.registerTextDestructor(
							context.node,
							context.gui_config,
							text_property,
							destructor,
							context.entry_keys
						);
						break;
					}
					default:
						unexpectedState($.properties.type.state);
				}
				break;
			}
			case "view": {
				break;
			}
			case "widget": {
				break;
			}
			case "window": {
				break;
			}
			default:
				unexpectedState(attribute.properties.type.state);
		}
	}
}

function walk_gui_widget_configuration_list(
	widget_list: api_views.Cgui_widget_configuration_list,
	on_item: (widget: api_views.Cgui_widget_configuration_node) => void
) {
	switch (widget_list.properties.has_steps.state.name) {
		case "no":
			break;
		case "yes": {
			const yes_has_steps = widget_list.properties.has_steps.state.node;
			on_item(yes_has_steps.properties.configuration);
			walk_gui_widget_configuration_list(yes_has_steps.properties.tail, on_item);
			break;
		}
		default:
			unexpectedState(widget_list.properties.has_steps.state);
	}
}

function bindToQuery(
	client_state_context: client_state_api.AlanObject,
	query_type: api_views.Cquery,
	destructor: DestructorTree
) {
	switch (query_type.properties.query_context.state.name) {
		case "node": {
			const node_type = query_type.properties.query_context.state.node;
			const context_node = ensure(client_state_context, client_state_api.Cnode);
			switch (node_type.properties.switch_.state.name) {
				case "root":
					initializeQueryWithModelBinding(context_node, node_type, destructor);
					break;
				case "current node": {
					const current_node_switch = node_type.properties.switch_.state.node;
					const expected_node_type = current_node_switch.properties.query.inferences
						.query()
						.properties.query_context.output.result_node()
						.input.application_node();
					const actual_type = context_node.input.node_type();
					if (expected_node_type !== actual_type) return;
					const new_node = registerModelBindingPropertyPath(
						node_type.properties.switch_.state.node.properties.property_path,
						context_node,
						destructor
					);
					initializeQueryWithModelBinding(new_node, node_type, destructor);
					break;
				}
				default:
					return unexpectedState(node_type.properties.switch_.state);
			}
			break;
		}
		case "candidates":
			// Candidate queries can not be added here because references nested
			// under matrix entries must be initialized seperatly.
			break;
		default:
			return unexpectedState(query_type.properties.query_context.state);
	}
	return;
}

function pathDomElementWithContext(
	parent_dom_element_constructor: () => HTMLElement,
	client_state_context: client_state_api.AlanObject
): () => HTMLElement {
	let created_element: HTMLElement;
	return () => {
		if (created_element !== undefined) {
			return created_element;
		}
		created_element = parent_dom_element_constructor();

		(created_element as any).alan_node = client_state_context;
		if (created_element.nodeName !== "#comment") {
			created_element.setAttribute("data-alan-context", client_state_context.path);
		}
		return created_element;
	};
}

function patchDomElementWithWidgetName(parent_dom_element: () => HTMLElement, key: string): () => HTMLElement {
	let created_element: HTMLElement;
	return () => {
		if (created_element === undefined) {
			created_element = parent_dom_element();
			if (created_element.nodeName !== "#comment") {
				created_element.setAttribute("data-widget", key);
			}
		}
		return created_element;
	};
}
